import { getGetServiceLogsUrl } from 'generatedTypes/asset/services/asset-service-log-controller';
import { useState } from 'react';

import useFetch from 'hooks/useFetch';
import usePagination from 'hooks/usePagination';
import useRetry from 'hooks/useRetry';

import useTranslation from './hooks/useTranslation';

import { DEFAULT_PAGE_SIZE } from './constants';
import type { AssetServiceLogResponse, FetchServiceLogs, FetchServiceLogsParams } from './types';

const useServiceLogs = () => {
    const [isLoading, setIsLoading] = useState(false);
    const { authenticatedGet } = useFetch();
    const { withRetry } = useRetry();
    const { CONNECTION_ERROR } = useTranslation();

    const fetchServiceLogs: FetchServiceLogs = async request => {
        const { assetId, ...params } = request;

        if (!assetId) {
            return null;
        }

        try {
            setIsLoading(true);
            const url = getGetServiceLogsUrl(assetId, params);

            return await withRetry(
                () => authenticatedGet(url),
                CONNECTION_ERROR,
                () => fetchServiceLogs(request)
            );
        } finally {
            setIsLoading(false);
        }
    };

    const {
        data: serviceLogsData,
        fetch: paginatedFetch,
        reset,
        totalCount
    } = usePagination<AssetServiceLogResponse>({
        callback: fetchServiceLogs
    });

    const handleFetch = (args: FetchServiceLogsParams) => {
        const { assetId, pageSize = DEFAULT_PAGE_SIZE, sort } = args;

        return paginatedFetch({ assetId, pageSize, sort });
    };

    return {
        fetch: handleFetch,
        isLoading,
        reset,
        serviceLogs: serviceLogsData as AssetServiceLogResponse[],
        totalCount
    };
};

export default useServiceLogs;
